import React, { useState, useEffect } from 'react';
import {
  Table, Button, Card, Space, Typography, Tag, Modal, Form, Input, Select,
  DatePicker, Switch, message, Popconfirm
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import campaignService from '../services/campaignService';
import moment from 'moment';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const CAMPAIGN_TYPES = {
  PROMOTION: { label: '限时促销', color: 'red' },
  ONGOING: { label: '常驻活动', color: 'blue' },
  EVENT: { label: '特别活动', color: 'purple' },
};

const Campaigns = () => {
  const { tenant } = useAuth();
  const tenantId = tenant?.id;

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [typeFilter, setTypeFilter] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1, pageSize: 20, total: 0,
    showSizeChanger: true, showQuickJumper: true,
    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
  });

  const fetchCampaigns = async (page = 1, pageSize = 20) => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const params = { tenantId, page: page - 1, size: pageSize };
      if (typeFilter) params.campaignType = typeFilter;
      const response = await campaignService.getCampaigns(params);
      if (response.success) {
        setCampaigns(response.data.content || []);
        setPagination(prev => ({ ...prev, current: page, pageSize, total: response.data.totalElements || 0 }));
      }
    } catch (error) {
      message.error('获取活动列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCampaigns(); }, [tenantId]);

  const handleCreate = () => {
    setEditingCampaign(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCampaign(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      campaignType: record.campaignType,
      dateRange: record.startDate && record.endDate ? [moment(record.startDate), moment(record.endDate)] : null,
      isDefault: record.isDefault,
      status: record.status,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const data = {
        tenantId,
        name: values.name,
        description: values.description,
        campaignType: values.campaignType,
        startDate: values.dateRange ? values.dateRange[0].format('YYYY-MM-DD') : null,
        endDate: values.dateRange ? values.dateRange[1].format('YYYY-MM-DD') : null,
        isDefault: values.isDefault || false,
        status: values.status || 'ACTIVE',
      };
      let response;
      if (editingCampaign) {
        response = await campaignService.updateCampaign(editingCampaign.id, data);
      } else {
        response = await campaignService.createCampaign(data);
      }
      if (response.success) {
        message.success(editingCampaign ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchCampaigns(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || '保存失败');
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await campaignService.deleteCampaign(id, tenantId);
      if (response.success) { message.success('删除成功'); fetchCampaigns(pagination.current, pagination.pageSize); }
      else message.error(response.message || '删除失败');
    } catch (error) { message.error('删除失败'); }
  };

  const columns = [
    { title: '活动名称', dataIndex: 'name', key: 'name', ellipsis: true },
    {
      title: '类型', dataIndex: 'campaignType', key: 'campaignType', width: 110,
      render: (type) => {
        const config = CAMPAIGN_TYPES[type] || { label: type, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    {
      title: '活动时间', key: 'dateRange', width: 220,
      render: (_, record) => {
        if (record.campaignType === 'ONGOING') return <Tag color="blue">长期有效</Tag>;
        if (record.startDate && record.endDate) return `${record.startDate} ~ ${record.endDate}`;
        return '-';
      }
    },
    {
      title: '默认', dataIndex: 'isDefault', key: 'isDefault', width: 70,
      render: (v) => v ? <Tag color="gold">默认</Tag> : '-'
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: (status) => <Tag color={status === 'ACTIVE' ? 'green' : 'default'}>{status === 'ACTIVE' ? '生效中' : '已停用'}</Tag>
    },
    { title: '描述', dataIndex: 'description', key: 'description', ellipsis: true },
    {
      title: '操作', key: 'action', width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除该活动？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>活动管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增活动</Button>
      </div>
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Select placeholder="按类型筛选" allowClear style={{ width: 150 }} onChange={(v) => { setTypeFilter(v); setTimeout(() => fetchCampaigns(1), 0); }}>
            {Object.entries(CAMPAIGN_TYPES).map(([key, val]) => <Option key={key} value={key}>{val.label}</Option>)}
          </Select>
        </Space>
        <Table columns={columns} dataSource={campaigns} rowKey="id" loading={loading} pagination={pagination}
          onChange={(pag) => fetchCampaigns(pag.current, pag.pageSize)} />
      </Card>

      <Modal title={editingCampaign ? '编辑活动' : '新增活动'} open={modalVisible} onOk={handleSave}
        onCancel={() => setModalVisible(false)} confirmLoading={saving} width={600} okText="保存" cancelText="取消">
        <Form form={form} layout="vertical" initialValues={{ status: 'ACTIVE', campaignType: 'PROMOTION' }}>
          <Form.Item name="name" label="活动名称" rules={[{ required: true, message: '请输入活动名称' }]}>
            <Input placeholder="如：新品上市8折优惠" maxLength={200} />
          </Form.Item>
          <Form.Item name="campaignType" label="活动类型" rules={[{ required: true }]}>
            <Select>
              {Object.entries(CAMPAIGN_TYPES).map(([key, val]) => <Option key={key} value={key}>{val.label}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="description" label="活动描述">
            <TextArea rows={3} placeholder="活动详情、规则说明等" />
          </Form.Item>
          <Form.Item name="dateRange" label="活动时间">
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
          <Space>
            <Form.Item name="isDefault" label="设为默认活动" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select style={{ width: 120 }}>
                <Option value="ACTIVE">生效中</Option>
                <Option value="INACTIVE">已停用</Option>
              </Select>
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default Campaigns;
